// Pure function: MobX message + pending-confirm snapshot → assistant-ui ThreadMessageLike.
// No mutation, no controller refs. Per design spec § "Message conversion".

const RESERVED = new Set(["id", "type", "safety_check"]);

function deriveArgs(action) {
  const args = Object.fromEntries(
    Object.entries(action).filter(([k]) => !RESERVED.has(k))
  );
  if (action.safety_check) {
    // Fallback-mode hint: renderer can read reason/token from args even if it
    // can't observe `status: requires-action` under ExternalStoreRuntime.
    args._safetyCheck = {
      reason: action.safety_check.reason,
      providerToken: action.safety_check.provider_token,
    };
  }
  return args;
}

function findResult(message, id) {
  return (message.actionResults || []).find((r) => r.id === id);
}

function statusFor(action, result, pendingConfirmIds) {
  if (result) return { type: "complete" };
  if (pendingConfirmIds.has(action.id)) return { type: "requires-action", reason: "safety_check" };
  return { type: "running" };
}

/**
 * @param {{ role: "user"|"assistant"|"system", text: string, actions?: object[], actionResults?: object[], screenshotUrl?: string }} message
 * @param {{ pendingConfirmIds: Set<string> }} ctx
 * @returns {{ role: string, content: object[] }}
 */
export function convertMessage(message, { pendingConfirmIds }) {
  // Screenshot of what the model saw on this turn — only meaningful for
  // assistant messages produced by the agent loop.
  const imagePart =
    message.role === "assistant" && message.screenshotUrl
      ? { type: "image", image: message.screenshotUrl }
      : null;

  if (message.role !== "assistant" || !message.actions || message.actions.length === 0) {
    const parts = [];
    if (imagePart) parts.push(imagePart);
    parts.push({ type: "text", text: message.text || "" });
    return { role: message.role, content: parts };
  }

  const toolCallParts = message.actions
    // `screenshot` is never a model-driven action under observation-as-result
    // (the harness captures automatically); don't surface a card for a stray one.
    .filter((action) => action.type !== "screenshot")
    .map((action) => {
    const result = findResult(message, action.id);
    const part = {
      type: "tool-call",
      toolCallId: action.id,
      toolName: action.type,
      args: deriveArgs(action),
      status: statusFor(action, result, pendingConfirmIds),
    };
    if (result) part.result = result;
    return part;
  });

  const parts = [];
  if (imagePart) parts.push(imagePart);
  if (message.text) parts.push({ type: "text", text: message.text });
  parts.push(...toolCallParts);
  return { role: "assistant", content: parts };
}
